import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    Inject,
    OnInit,
    PLATFORM_ID,
    ViewChild,
    ElementRef,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';

// ** rxjs
import { Observable, of } from 'rxjs';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';

// ** service
import { LayoutService } from '@app/core/services/layout.service';
import { ProductsService } from '@app/core/services/products.service';
import { TranslateService } from '@ngx-translate/core';

// * primeng
import { Accordion } from 'primeng/accordion';
import { Paginator } from 'primeng/paginator';

@Component({
    selector: 'app-products',
    templateUrl: './products.component.html',
    styleUrls: ['./products.component.scss'],
})
export class ProductsComponent implements OnInit {
    @ViewChild(Paginator) paginator: Paginator;
    @ViewChild(Accordion) productAcc: Accordion;
    @ViewChild('kw') searchInput: ElementRef;

    isBrowser: boolean;
    scrollPosition: number = 0;
    currentSection = 0;
    activeIdx = 0;
    // 每頁幾筆
    rowsPerPage = 6;

    stackProgress = {
        zeroToOne: 'width-0',
        oneToTwo: 'width-0',
        twoToThree: 'width-0',
        threeToFour: 'width-0',
        fourToFive: 'width-0',
        fiveToSix: 'width-0',
    };

    // ** 搜尋欄位的值
    keywordRaw = '';
    // ** 推薦關鍵字 tag
    recommendTags = [
        'bigData',
        'iot',
        'ai',
        'infoSecurity',
        'dataIntegration',
        'nationalSecurity',
        'informationSecurity',
        'publicSecurity',
    ];

    private tagSearchMapping = {
        'Information Security': 'Cybersecurity',
        InfoSec: 'Information Security',
        AI: 'Artificial Intelligence',
    };

    // ** 所有的 products 清單 (全部)
    productsSheet = [];
    // ** 所有的 products 清單 (by 關鍵字搜尋)
    productsByKeyword = [];
    // ** 當前應呈現的 product 資料
    productsByPage = [];
    // 儲存當前關鍵字
    private currentParamKeyword: string = '';

    // ** url 可能會帶入的 param，map 中文
    typeMap = {
        'big-data': '大數據',
        iot: '物聯網',
        ai: '人工智慧',
        'info-security': '資訊安全',
        'digital-surveillance': '安全監控',
        'public-security': '治安',
        'data-consolidation': '數據整合',
        'technical-support': '技術支援',
    };

    // ** 右側搜尋結果區塊，固定順序顯示底圖
    imgMap = [
        '/assets/images/products/product-ky-template-1.png',
        '/assets/images/products/product-ky-template-2.png',
        '/assets/images/products/product-ky-template-3.png',
        '/assets/images/products/product-ky-template-4.png',
        '/assets/images/products/product-ky-template-5.png',
    ];

    // @HostListener('window:scroll', [])
    // onWindowScroll(e) {
    // 	if (this.isBrowser) {
    // 		// ** the first section
    // 		let bannerHeight = document.getElementById('bannerArea').offsetHeight / 6;
    // 		let base = window.innerHeight - bannerHeight;
    // 		let scroll = window.scrollY + 5;
    // 		this.currentSection = Math.floor(scroll / base);
    // 		this.activeIdx = this.currentSection - 1;
    // 		this.cdr.detectChanges();
    // 		let progressInCurrentSection = ((scroll % base) / base) * 100;

    // 		Object.keys(this.stackProgress).forEach((key, idx) => {
    // 			if (idx + 1 < this.currentSection) return (this.stackProgress[key] = `width-100`);
    // 			if (idx + 1 === this.currentSection)
    // 				return (this.stackProgress[key] = `width-${Math.ceil(progressInCurrentSection)}`);
    // 			this.stackProgress[key] = `width-0`;
    // 		});

    // 		this.productAcc.el.nativeElement.lastElementChild.className = `p-accordion p-component ${this.stackProgress.fiveToSix}`;
    // 	}
    // }

    constructor(
        @Inject(DOCUMENT) private document: Document,
        @Inject(PLATFORM_ID) private platformId,
        private route: ActivatedRoute,
        private title: Title,
        private router: Router,
        private productsService: ProductsService,
        private layoutService: LayoutService,
        private translateService: TranslateService,
        private cdr: ChangeDetectorRef,
    ) {
        this.isBrowser = isPlatformBrowser(this.platformId);
        if (this.isBrowser) this.scrollPosition = window.scrollY;

        // 監聽語系變化，清空輸入框(考量 user 可自行輸入，輸入內容無法對應預設關鍵字語系轉換)
        this.translateService.onLangChange.subscribe(() => {
            this.clearSearchOnLangChange();
        });
    }

    ngOnInit(): void {
        const canonical = this.document.querySelector(
            'link[rel="canonical"]',
        ) as HTMLAnchorElement | null;
        if (canonical)
            canonical.href = 'https://www.kunyoutech.com.tw/products';

        this.initPage();
    }

    initPage() {
        this.route.queryParams
            .pipe(
                tap(() => this.layoutService.setPageLoading(true)),
                tap(params => {
                    this.currentParamKeyword = params['keyword'] || '';
                    console.log(this.currentParamKeyword);
                    this.keywordRaw = this.currentParamKeyword
                        ? this.getTranslatedTypeFromParam(
                              this.currentParamKeyword,
                          )
                        : '';
                }),
                tap(() => this.getProductsSheet()),
                finalize(() => this.layoutService.setPageLoading(false)),
            )
            .subscribe();
    }

    // dynamicToSection(idx) {
    // 	if (!this.isBrowser) return;
    // 	this.activeIdx = idx;
    // }

    // - 取得所有資料 (進到頁面只呼叫一次)
    getProductsSheet() {
        of(this.layoutService.setPageLoading(true))
            .pipe(
                switchMap(() => this.getProductsSheet$()),
                tap(res => (this.productsSheet = [...res])),
                tap(res => (this.productsByKeyword = [...res])),
                filter(() =>
                    this.keywordRaw
                        ? !!(this.getProductByKeyword(
                              this.keywordRaw,
                              null,
                          ) as any)
                        : true,
                ),
                tap(
                    res =>
                        (this.productsByPage = [...res].slice(
                            0,
                            this.rowsPerPage,
                        )),
                ),
                finalize(() => this.layoutService.setPageLoading(false)),
            )
            .subscribe();
    }

    /** 按下搜尋 - 取交集 */
    getProductByKeyword(rawString, e) {
        of(this.layoutService.setPageLoading(true))
            .pipe(
                tap(() => {
                    const mappedString =
                        this.tagSearchMapping[rawString] || rawString;
                    const keywords = (mappedString ?? '')
                        .split(' ')
                        .filter(k => k.trim() !== '');
                    if (!keywords.length) {
                        this.productsByKeyword = [...this.productsSheet];
                        return;
                    }

                    const keywordRegexes = keywords.map(
                        k => new RegExp(k, 'i'),
                    );

                    // 根據當前語系選擇要搜尋的 MetaTags
                    const currentLang = this.translateService.currentLang;
                    const isEnglish = currentLang === 'en-us';

                    this.resetProductsData(e);
                    this.productsSheet.forEach(prod => {
                        const metaTags = isEnglish
                            ? prod.MetaTagsEN ?? []
                            : prod.MetaTags ?? [];
                        const name = prod.Name ?? '';
                        // 取 交集
                        const isRelated = keywordRegexes.every(
                            regex =>
                                metaTags.some(tag => regex.test(tag)) ||
                                regex.test(name),
                        );

                        if (isRelated) {
                            this.productsByKeyword.push(prod);
                        }
                    });
                }),
                tap(() => this.getDataByPage({ page: 0 })),
                finalize(() => this.layoutService.setPageLoading(false)),
            )
            .subscribe();
    }

    // - 分頁
    getDataByPage(event) {
        const rowsPerPage = event.rows ?? this.rowsPerPage;
        of(this.layoutService.setPageLoading(true))
            .pipe(
                tap(() => {
                    this.productsByPage = this.productsByKeyword.slice(
                        event.page * rowsPerPage,
                        (event.page + 1) * rowsPerPage,
                    );
                }),
                tap(() => {
                    if (!this.isBrowser) return;
                    let element = document.getElementById('exploreArea');
                    let targetPosition = element.offsetTop - 65;
                    setTimeout(
                        () =>
                            window.scrollTo({
                                top: targetPosition,
                                behavior: 'smooth',
                            }),
                        250,
                    );
                }),
                finalize(() => this.layoutService.setPageLoading(false)),
            )
            .subscribe();
    }

    // - 前往產品內頁
    goToProduct(NameEN) {
        of(this.layoutService.setPageLoading(true))
            .pipe(
                tap(() =>
                    setTimeout(() =>
                        this.router.navigateByUrl(`products/${NameEN}`),
                    ),
                ),
                finalize(() => this.layoutService.setPageLoading(false)),
            )
            .subscribe();
    }

    // - reset 產品呈現資料 to default
    resetProductsData(e) {
        this.productsByKeyword = [];
        this.productsByPage = [];

        if (e) this.paginator?.changePageToFirst(e);
    }

    /**
     * 根據參數取翻譯後的(從 typeMap 取並帶入搜尋框的關鍵字)
     */
    getTranslatedTypeFromParam(paramKey: string): string {
        console.log(paramKey);
        if (!paramKey || !this.typeMap[paramKey]) {
            return '';
        }
        console.log(paramKey);
        // 例如：'big-data' -> 'bigData', 'iot' -> 'iot'
        const camelCaseKey = paramKey.replace(/-([a-z])/g, (match, letter) => letter.toUpperCase());
        const translationKey = `products.search.inputTypesSearch.${camelCaseKey}`;
        const translated = this.translateService.instant(translationKey);
        return translated !== translationKey
            ? translated
            : this.typeMap[paramKey];
    }

    /**
     * 英文名稱轉換為對應的 key
     * ex：'ai-license-plate-recognition-system' -> 'aiLicensePlateRecognitionSystem'
     */
    getProductKeyFromNameEN(nameEN: string): string {
        return nameEN
            .replace(/-([a-z])/g, (match, letter) => letter.toUpperCase())
            .replace(/^[a-z]/, letter => letter.toLowerCase());
    }

    // 翻譯後的 tag 文字（用於搜尋）
    getTranslatedTagForSearch(tagKey: string): string {
        return this.translateService.instant(
            `products.search.recommendTags.${tagKey}`,
        );
    }

    // 加入 tag 到搜尋框
    addTagToKeyword(tagKey: string): void {
        const translatedTag = this.getTranslatedTagForSearch(tagKey).trim();
        if (!translatedTag) return;

        const currentKeyword = (this.keywordRaw ?? '').trim().replace(/\s+/g, ' ');
        // 避免重複加入同一個 tag
        const normalizedKeyword = ` ${currentKeyword.toLowerCase()} `;
        const normalizedTag = ` ${translatedTag.toLowerCase()} `;
        const isExistingTag = normalizedKeyword.includes(normalizedTag);

        this.keywordRaw = isExistingTag
            ? currentKeyword
            : currentKeyword
              ? `${currentKeyword} ${translatedTag}`
              : translatedTag;
    }

    // 語系切換時清空搜尋框
    clearSearchOnLangChange() {
        this.keywordRaw = '';
        if (this.searchInput?.nativeElement) {
            this.searchInput.nativeElement.value = '';
        }
    }

    /* -------------------------------------------------------------------------- */
    /*                                     api                                    */
    /* -------------------------------------------------------------------------- */

    getProductsSheet$(): Observable<any> {
        return this.productsService.getProductsSheet().pipe();
    }
}
